import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook to manage bot configuration history in localStorage.
 * Handles saving successful launches and retrieving them for "Quick Start".
 */
const HISTORY_KEY = 'kahoot_bot_history';

export const useGameHistory = () => {
    const [history, setHistory] = useState([]);

    /**
     * Loads history from localStorage and sorts it by timestamp (descending).
     */
    const loadHistory = useCallback(() => {
        try {
            const data = localStorage.getItem(HISTORY_KEY);
            let localHistory = [];
            if (data) {
                const parsed = JSON.parse(data);
                if (Array.isArray(parsed)) {
                    localHistory = parsed;
                }
            }
            
            // Sort local history
            const sorted = localHistory.sort((a, b) => b.timestamp - a.timestamp);
            setHistory(sorted);
        } catch (e) {
            console.error("Failed to load history", e);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    /**
     * Adds a new entry to the history. 
     * Removes duplicates (same settings) before adding to keep it clean.
     * @param {Object} entry - The bot configuration to save.
     */
    const addToHistory = useCallback((entry) => {
        try {
            const data = localStorage.getItem(HISTORY_KEY);
            let current = data ? JSON.parse(data) : [];
            
            if (!Array.isArray(current)) current = [];

            // Remove any existing entry with the exact same configuration to avoid clutter
            current = current.filter(item => 
                !(item.pin === entry.pin && 
                  item.name === entry.name && 
                  item.amount === entry.amount && 
                  item.teamMode === entry.teamMode)
            );

            const newEntry = {
                ...entry,
                timestamp: Date.now()
            };

            // Keep only the latest 10 entries in storage
            const updated = [newEntry, ...current].slice(0, 10);
            localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
            
            setHistory(updated);
        } catch (e) {
            console.error("Failed to save history", e);
        }
    }, []);

    /**
     * Formats a timestamp into a human-readable relative time string.
     * @param {number} timestamp - The Unix timestamp to format.
     * @returns {string} Relative time (e.g., "just now", "5m ago").
     */
    const formatRelativeTime = (timestamp) => {
        const diff = Date.now() - timestamp;
        const mins = Math.floor(diff / 60000);
        
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    return { history, addToHistory, formatRelativeTime };
};
